import React, { useState, useContext, useEffect } from 'react'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons'
import { faUser } from '@fortawesome/free-regular-svg-icons'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import UsersDropdown from 'shared/forms/users-dropdown'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import { useQueryClient } from 'react-query'
import MutedText from 'shared/forms/muted-text'

const ShareButton = ({
  resource,
  resourceName,
  useCreateMutation,
  createParams,
  queryKeys = [],
  UserRowComponent,
  userRowProps = () => ({}),
  buttonText,
  iconOnly = false,
  size = 'sm',
  triggerClassName = 'bg-light text-dark',
  // Controlled mode: pass `show`/`onHide` and no trigger renders, so a menu item elsewhere (the
  // Views index's per-view menu) can open the modal.
  show: controlledShow,
  onHide
}) => {
  const queryClient = useQueryClient()
  const isControlled = controlledShow !== undefined
  const [internalShow, setInternalShow] = useState(false)
  const show = isControlled ? controlledShow : internalShow
  const handleClose = () => (isControlled ? onHide?.() : setInternalShow(false))
  const handleShow = () => setInternalShow(true)
  const [userOption, setUserOption] = useState('')
  const user = useContext(UserContext)
  const isUserCreator = resource?.user_id === user.id
  const sharedUserIds = resource?.shared_users?.map((u) => u.user_id) || []
  const allUsers = sharedUserIds.concat(user.id)

  const params = {
    user_id: userOption,
    ...createParams
  }

  const options = {
    onSuccess: () => {
      queryKeys.forEach((key) => queryClient.invalidateQueries(key))
    }
  }

  const {
    mutate: createMutation,
    isLoading,
    isError,
    isSuccess
  } = useCreateMutation({ params, options })

  useEffect(() => {
    setUserOption('')
  }, [isSuccess])

  if (!resource) {
    return ''
  }

  const sharedCount = resource.shared_users?.length || 0

  const renderTrigger = () => {
    if (iconOnly) {
      if (isUserCreator && sharedCount > 0) {
        return (
          <span
            className="text-muted small"
            style={{ cursor: 'pointer' }}
            data-testid="share-button"
            onClick={handleShow}
          >
            <FontAwesomeIcon icon={faUser} /> {sharedCount}
          </span>
        )
      }
      return null
    }

    if (!isUserCreator) return null

    return (
      <Button
        variant="light"
        size={size}
        data-testid="share-button"
        className={triggerClassName}
        onClick={handleShow}
      >
        <FontAwesomeIcon icon={sharedCount === 0 ? faUserPlus : faUsers} className="me-1" />
        {buttonText && ` ${buttonText}`}
        {sharedCount > 0 ? ` ${sharedCount}` : ''}
      </Button>
    )
  }

  return (
    <>
      {isError && toast.error('An error occured')}
      {!isControlled && renderTrigger()}
      <Modal show={show} size="md" onHide={handleClose}>
        <Modal.Header className="fw-light d-flex flex-row justify-content-between">
          <div className="d-flex fs-5">
            <span className="fw-normal me-2">Share {resourceName}</span>
          </div>
          <div className="d-flex">
            <Button variant="outline-primary" size="sm" onClick={handleClose}>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {isUserCreator && (
            <Row>
              <Col sm={8}>
                <UsersDropdown
                  params={userOption}
                  setParams={setUserOption}
                  isObject={false}
                  disabled={allUsers}
                  sectionId={resource.section_id}
                />
              </Col>
              <Col sm={4}>
                <Button
                  variant="primary"
                  onClick={() => createMutation()}
                  disabled={userOption === '' || isLoading}
                >
                  <FontAwesomeIcon icon={faPlus} className="text-warning" /> Add
                  User
                </Button>
              </Col>
            </Row>
          )}

          <h4 className="my-2">People with access</h4>
          <Table className="border border-light">
            <tbody className="border-top">
              <tr className="bg-light">
                <td colSpan={2}>
                  {resource.owner.first_name} {resource.owner.last_name}{' '}
                  <MutedText as="div">
                    <small>{resource.owner.email}</small>
                  </MutedText>
                  <small className="ms-1 text-muted">Owner</small>
                </td>
              </tr>
              {resource.shared_users?.map((u) => (
                <UserRowComponent
                  key={`shared-user-${u.id}`}
                  {...userRowProps(u)}
                />
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ShareButton
